import styled, { css, keyframes } from 'styled-components';
import { TOAST_DURATION_IN_SECONDS } from './utils/constants.js';
import { themeGet, themeGetColor } from '../../../../foundation/theme/themeGet/get.js';
import { pxToRem } from '../../../../foundation/spacing/utils.js';
import { Typography } from '../../../../foundation/Typography/styled.js';
import { IconButton } from '../../../../navigation/buttons/IconButton/IconButton.js';

function slideInAndOutAnimation(anchorOrigin) {
  switch (anchorOrigin) {
    case 'top-center':
      return keyframes(["0%{transform:translate(-50%,calc(-1 * ", "));opacity:0;}10%{transform:translate(-50%,0);opacity:1;}90%{transform:translate(-50%,0);opacity:1;}100%{transform:translate(-50%,calc(-1 * ", "));opacity:0;}"], pxToRem(60), pxToRem(60));
    case 'top-left':
    case 'top-right':
      return keyframes(["0%{transform:translate(0,calc(-1 * ", "));opacity:0;}10%{transform:translate(0,0);opacity:1;}90%{transform:translate(0,0);opacity:1;}100%{transform:translate(0,calc(-1 * ", "));opacity:0;}"], pxToRem(60), pxToRem(60));
    case 'bottom-left':
    case 'bottom-right':
      return keyframes(["0%{transform:translate(0,", ");opacity:0;}10%{transform:translate(0,0);opacity:1;}90%{transform:translate(0,0);opacity:1;}100%{transform:translate(0,", ");opacity:0;}"], pxToRem(60), pxToRem(60));
    case 'bottom-center':
    default:
      return keyframes(["0%{transform:translate(-50%,", ");opacity:0;}10%{transform:translate(-50%,0);opacity:1;}90%{transform:translate(-50%,0);opacity:1;}100%{transform:translate(-50%,", ");opacity:0;}"], pxToRem(60), pxToRem(60));
  }
}
function toastAnchorOrigin(anchorOrigin) {
  switch (anchorOrigin) {
    case 'top-left':
      return css(["left:", ";top:", ";"], pxToRem(20), pxToRem(20));
    case 'top-center':
      return css(["left:50%;top:", ";"], pxToRem(20));
    case 'top-right':
      return css(["right:", ";top:", ";"], pxToRem(20), pxToRem(20));
    case 'bottom-right':
      return css(["right:", ";bottom:", ";"], pxToRem(20), pxToRem(20));
    case 'bottom-left':
      return css(["left:", ";bottom:", ";"], pxToRem(20), pxToRem(20));
    case 'bottom-center':
    default:
      return css(["left:50%;bottom:", ";"], pxToRem(20));
  }
}
var Container = styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-rvlkiu-0"
})(["display:flex;flex-direction:column;gap:", ";position:fixed;z-index:3;", ";padding:", ";background-color:", ";box-shadow:", ";", ";min-width:0;width:", ";max-width:", ";border-radius:", ";"], themeGet('spacing.md'), function (props) {
  return toastAnchorOrigin(props.$anchorOrigin);
}, themeGet('spacing.xl'), function (props) {
  return props.$lightMode ? themeGet('colors.background.50') : themeGetColor('primary.1000', 0.85);
}, themeGet('dropShadow.100'), function (props) {
  return css(["animation:", "s ", " forwards;"], TOAST_DURATION_IN_SECONDS, slideInAndOutAnimation(props.$anchorOrigin));
}, pxToRem(340), pxToRem(340), themeGet('borderRadius.md'));
var IconContainer = styled.div.withConfig({
  displayName: "IconContainer",
  componentId: "sc-rvlkiu-1"
})(["display:flex;flex-direction:column;justify-content:center;"]);
var ToastHeader = styled.div.withConfig({
  displayName: "ToastHeader",
  componentId: "sc-rvlkiu-2"
})(["display:flex;flex-direction:row;justify-content:flex-start;align-items:center;gap:", ";"], themeGet('spacing.md'));
var ToastTitle = styled(Typography).withConfig({
  displayName: "ToastTitle",
  componentId: "sc-rvlkiu-3"
})(["display:flex;flex:1;color:", ";"], function (props) {
  return props.$lightMode ? themeGet('colors.text.300') : themeGet('colors.text.light');
});
var ToastMessage = styled(Typography).withConfig({
  displayName: "ToastMessage",
  componentId: "sc-rvlkiu-4"
})(["color:", ";"], function (props) {
  return props.$lightMode ? themeGet('colors.text.100') : themeGet('colors.primary.200');
});
var ToastDate = styled(Typography).withConfig({
  displayName: "ToastDate",
  componentId: "sc-rvlkiu-5"
})(["color:", ";"], function (props) {
  return props.$lightMode ? themeGet('colors.primary.500') : themeGet('colors.primary.400');
});
var ToastCloseButton = styled(IconButton).withConfig({
  displayName: "ToastCloseButton",
  componentId: "sc-rvlkiu-6"
})(["", ""], function (props) {
  return props.$lightMode !== true && css(["[data-icon='falcon']{color:", ";}&&:hover{background-color:", ";[data-icon='falcon']{color:", ";}}&&:active{background-color:", ";[data-icon='falcon']{color:", ";}}&&:focus-visible{text-decoration:underline;background-color:", ";box-shadow:0 0 0 ", " ", ";}"], themeGet('colors.primary.50'), themeGet('colors.primary.800'), themeGet('colors.primary.50'), themeGet('colors.primary.700'), themeGet('colors.primary.50'), themeGet('colors.primary.800'), pxToRem(4), themeGet('colors.focus.primary'));
});
var ToastBody = styled.div.withConfig({
  displayName: "ToastBody",
  componentId: "sc-rvlkiu-7"
})(["display:flex;flex-direction:column;gap:", ";margin-left:", ";"], themeGet('spacing.xl'), themeGet('spacing.3xl'));
var UndoButton = styled.button.withConfig({
  displayName: "UndoButton",
  componentId: "sc-rvlkiu-8"
})(["border:0;margin:0;padding:0;padding-right:", ";background-color:transparent;cursor:pointer;&:hover{text-decoration:underline;}", ";"], themeGet('spacing.xl'), function (props) {
  return props.$lightMode ? css(["color:", ";border-right:", " solid ", ";"], themeGet('colors.secondary.500'), pxToRem(1), themeGet('colors.border.100')) : css(["color:", ";border-right:", " solid ", ";"], themeGet('colors.primary.200'), pxToRem(1), themeGet('colors.gray.300'));
});

export { Container, IconContainer, ToastBody, ToastCloseButton, ToastDate, ToastHeader, ToastMessage, ToastTitle, UndoButton };
